import { Directive, Inject, Input, OnInit } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ListViewComponent } from '@y42/core/list-view';
import { environment } from '@y42/environment';
import { ErrorHandlerService } from '@y42/error-handling';
import { assertNonNullable } from '@y42/util/assert';
import { tap } from 'rxjs';
import { FeatureChildAggregationService } from '../../api/feature-child-aggregation.service';
import { AbstractFeatureChildUIModel, AbstractFeatureStoreModel, AbstractFeatureUIModel } from '../../models';
import { Y42Route, Y42RouteDetailTabs, Y42RouterService, Y42_ROUTE } from '../../routing';
import { FeatureListViewData } from './abstract-feature-list-view.service.model';

@UntilDestroy()
@Directive({ selector: 'y42-list-view[y42RedirectToDetailOnClick]' })
export class FeatureListViewRedirectOnClickDirective implements OnInit {
  constructor(
    private readonly listViewComponent: ListViewComponent<
      FeatureListViewData<AbstractFeatureUIModel, AbstractFeatureChildUIModel>
    >,
    private readonly routerService: Y42RouterService,
    private readonly featureChildAggregationService: FeatureChildAggregationService,
    private readonly errorHandlerService: ErrorHandlerService,
    @Inject(Y42_ROUTE) private readonly route: Y42Route,
  ) {}

  @Input() y42RedirectToDetailChildType?: 'route' | 'queryparam' | false = false;
  @Input() y42RedirectToDetailChildQueryParam?: string;

  ngOnInit() {
    this.navigateToDetailViewOnGridRowClick().subscribe();
  }

  private navigateToDetailViewOnGridRowClick() {
    return this.listViewComponent.gridRowClicked.pipe(
      tap((evt) => {
        const level = evt.node.level;
        const row: FeatureListViewData<AbstractFeatureUIModel, AbstractFeatureChildUIModel> | undefined = evt.data;
        const $path: AbstractFeatureStoreModel['$path'] = row?.$path || evt.node.aggData.id;
        const $name = $path.split('/').pop();

        if (!$name) {
          this.errorHandlerService.error(
            '[FeatureListViewRedirectOnClickDirective]: error redirecting to detail-view',
            {
              context: { row, aggData: evt.node.aggData },
              toast: `Could not redirect to item`,
            },
          );
          return;
        }

        if (level === 0 || !this.y42RedirectToDetailChildType) {
          return this.routerService.navigateToDetail(this.route, $name);
        } else if (level === 1) {
          const child_$path = row!.child_$path;
          const activeFeature = this.featureChildAggregationService.getActiveFeatureFromURL();
          const childEntity = activeFeature?.query.getEntity(child_$path);

          if (this.y42RedirectToDetailChildType === 'route') {
            return this.routerService.navigateToDetail(
              this.route,
              $name,
              Y42RouteDetailTabs.Overview,
              childEntity!.$name,
            );
          } else if (this.y42RedirectToDetailChildType === 'queryparam') {
            const queryParam = this.y42RedirectToDetailChildQueryParam;
            assertNonNullable(queryParam);
            return this.routerService.navigateToDetail(this.route, $name, { [queryParam]: childEntity!.$name });
          }
        }

        if (!environment.production_build) {
          console.warn('[FeatureListViewRedirectOnClickDirective]: redirect from this level not supported');
        }

        return;
      }),
      untilDestroyed(this),
    );
  }
}
